<template>
  <v-container class="py-12">
    <div class="text-center mb-12">
      <h1 class="text-h4 text-sm-h3">
        {{ step === 'done' ? 'Succès' : 'Bienvenue' }}
      </h1>

      <div class="subtitle-1 text--secondary mt-2">
        <template v-if="step !== 'done'">
          Créez un compte pour postuler aux offres d'emplois
        </template>

        <template v-else>
          Un e-mail a été envoyé à l'adresse : {{ email }}
          <br>
          Veuillez consulter votre boîte de réception pour activer votre compte
          <br>
          Aucun email reçu ?
          <router-link to="/verify-email">Cliquez-ici</router-link>
        </template>
      </div>

      <template v-if="step === 'done'">
        <v-img
          height="200"
          class="mt-6 mb-12"
          contain
          :src="require('@/assets/undraw_winners_ao2o.svg')"
        ></v-img>

        <v-btn
          color="primary"
          class="pa-6"
          to="/"
        >
          Accueil
        </v-btn>
      </template>
    </div>

    <template v-if="step !== 'done'">
      <v-card
        width="400"
        class="mx-auto rounded-lg"
        :loading="loading"
      >
        <v-card-title class="justify-center pb-0">
          <v-img
            :src="require('@/assets/undraw_Login_re_4vu2.svg')"
            height="100"
            contain
          ></v-img>
          {{ formTitle }}
        </v-card-title>

        <v-window v-model="step" touchless>
          <v-window-item :value="1">
            <v-card-text class="pb-0">
              <v-form ref="firstForm" @submit.prevent>
                <v-text-field
                  label="Prénom"
                  v-model="firstName"
                  :rules="rules.firstName"
                  validate-on-blur
                  outlined
                  dense
                ></v-text-field>

                <v-text-field
                  label="Nom"
                  v-model="lastName"
                  :rules="rules.lastName"
                  validate-on-blur
                  outlined
                  dense
                ></v-text-field>

                <v-text-field
                  label="Adresse e-mail"
                  v-model="email"
                  :rules="rules.email"
                  validate-on-blur
                  :error-messages="emailErrorText"
                  @change="emailErrorText = ''"
                  type="email"
                  outlined
                  dense
                ></v-text-field>

                <v-text-field
                  label="Mot de passe"
                  v-model="password"
                  :rules="rules.password"
                  validate-on-blur
                  :type="passIsVisible ? 'text' : 'password'"
                  :append-icon="passIsVisible ? 'mdi-eye-off' : 'mdi-eye'"
                  @click:append="passIsVisible = !passIsVisible"
                  outlined
                  dense
                ></v-text-field>

                <v-text-field
                  label="Confirmer"
                  v-model="confirmPassword"
                  :rules="rules.confirmPassword"
                  validate-on-blur
                  :type="passIsVisible ? 'text' : 'password'"
                  :append-icon="passIsVisible ? 'mdi-eye-off' : 'mdi-eye'"
                  @click:append="passIsVisible = !passIsVisible"
                  outlined
                  dense
                ></v-text-field>
              </v-form>
            </v-card-text>
          </v-window-item>

          <v-window-item :value="2">
            <v-card-text class="pb-0">
              <v-form ref="secondForm" @submit.prevent>
                <v-radio-group
                  label="Sexe"
                  v-model="gender"
                  mandatory
                  row
                  class="mt-0 pt-2 pb-3"
                >
                  <v-radio
                    label="Homme"
                    value="M"
                  >
                  </v-radio>

                  <v-radio
                    label="Femme"
                    value="F"
                  >
                  </v-radio>
                </v-radio-group>

                <v-menu
                  ref="birthMenu"
                  v-model="birthMenuIsActive"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  min-width="auto"
                  offset-y
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      label="Date de naissance"
                      :value="birthDate | formatDate"
                      :rules="rules.birthDate"
                      outlined
                      dense
                      readonly
                      v-bind="attrs"
                      v-on="on"
                    ></v-text-field>
                  </template>

                  <v-date-picker
                    ref="birthPicker"
                    v-model="birthDate"
                    :max="new Date(new Date().setFullYear(
                      new Date().getFullYear() - 18)).toISOString().substr(0, 10)"
                    min="1950-01-01"
                    @change="saveBirth"
                    no-title
                    first-day-of-week="1"
                  ></v-date-picker>
                </v-menu>

                <v-autocomplete
                  label="Nationalité"
                  v-model="nationality"
                  :rules="rules.nationality"
                  validate-on-blur
                  :items="nationalities"
                  outlined
                  dense
                ></v-autocomplete>

                <v-text-field
                  label="N° de téléphone"
                  v-model="mobile"
                  :rules="rules.mobile"
                  validate-on-blur
                  type="tel"
                  outlined
                  dense
                ></v-text-field>
              </v-form>
            </v-card-text>
          </v-window-item>

          <v-window-item :value="3">
            <v-card-text class="pb-0">
              <v-form ref="thirdForm" @submit.prevent>
                <v-text-field
                  label="Adresse"
                  v-model="streetAndNumber"
                  :rules="rules.streetAndNumber"
                  validate-on-blur
                  outlined
                  dense
                ></v-text-field>

                <v-text-field
                  label="Code Postal"
                  v-model="zipCode"
                  :rules="rules.zipCode"
                  validate-on-blur
                  outlined
                  dense
                ></v-text-field>

                <v-text-field
                  label="Ville"
                  v-model="city"
                  :rules="rules.city"
                  validate-on-blur
                  outlined
                  dense
                ></v-text-field>

                <v-autocomplete
                  label="Pays"
                  v-model="country"
                  :rules="rules.country"
                  validate-on-blur
                  :items="countries"
                  item-text="country_name"
                  item-value="country_name"
                  outlined
                  dense
                ></v-autocomplete>

                <v-checkbox
                  v-model="userAgree"
                  :rules="[(v) => !!v || 'Vous devez cocher cette case pour continuer']"
                  class="mt-0"
                >
                  <template v-slot:label>
                    <div class="caption">
                      J'accepte les conditions des
                      <a
                        href="javascript:;"
                        @click.stop="$emit('open-legal-notice')"
                      >
                        mentions légales
                      </a>
                    </div>
                  </template>
                </v-checkbox>
              </v-form>
            </v-card-text>
          </v-window-item>
        </v-window>

        <v-card-text class="pt-0">
          <v-btn
            @click="next"
            color="primary"
            block
          >
            {{ step === 3 ? 'Créer mon compte' : 'Suivant' }}
          </v-btn>

          <v-btn
            @click="step -= 1"
            v-if="step > 1"
            color="secondary"
            block
            outlined
            class="mt-2"
          >
            Retour
          </v-btn>
        </v-card-text>
      </v-card>

      <div class="mt-6 text-center">
        <span>Déjà un compte ? </span>
        <router-link to="/login">Connectez-vous !</router-link>
      </div>
    </template>
  </v-container>
</template>

<script>
import rulesMixin from '@/mixins/rulesMixin';

const axios = require('axios').default;

export default {
  name: 'Register',

  mixins: [rulesMixin],

  created() {
    this.getCountries();
  },

  data: () => ({
    birthMenuIsActive: false,
    step: 1,
    loading: false,
    countries: [],
    emailErrorText: '',
    passIsVisible: false,
    lastName: '',
    firstName: '',
    email: '',
    password: '',
    confirmPassword: '',
    gender: '',
    birthDate: '',
    nationality: '',
    mobile: '',
    streetAndNumber: '',
    zipCode: '',
    city: '',
    country: '',
    userAgree: false,
  }),

  computed: {
    formTitle() {
      switch (this.step) {
        case 1: return 'Inscription';
        case 2: return 'Complétez vos données';
        default: return 'Encodez votre adresse';
      }
    },
    nationalities() {
      const data = this.countries.map((item) => item.nationality);
      return data.sort((a, b) => a.localeCompare(b));
    },
  },

  methods: {
    async getCountries() {
      try {
        const response = await axios.get('countries');
        this.countries = response.data;
      } catch (error) {
        this.$store.commit('SET_SNACKBAR', {
          isActive: true,
          text: 'Une erreur est survenue, rechargez la page',
        });
      }
    },

    next() {
      if (
        (this.step === 1 && this.$refs.firstForm.validate())
        || (this.step === 2 && this.$refs.secondForm.validate())
      ) {
        this.step += 1;
      } else if (this.step === 3 && this.$refs.thirdForm.validate()) {
        this.sendForm();
      }
    },

    async sendForm() {
      try {
        this.loading = true;
        await axios.post('candidats', {
          candidat: {
            nom: this.lastName,
            prenom: this.firstName,
            email: this.email,
            validation_email: this.email,
            password: this.password,
            password_confirmation: this.confirmPassword,
            genre: this.gender,
            date_naissance: this.birthDate,
            nationalite: this.nationality,
            tel: this.mobile,
            adresse: this.streetAndNumber,
            code_postal: this.zipCode,
            ville: this.city,
            pays: this.country,
            statut_email: 0,
          },
        });
        this.step = 'done';
      } catch (error) {
        this.loading = false;
        if (error.response.status === 422 && error.response.data.email) {
          this.emailErrorText = 'Cet email est déjà pris';
          this.step = 1;
        } else {
          this.$store.commit('SET_SNACKBAR', {
            isActive: true,
            text: 'Une erreur est survenue, votre compte n\'a pas pu être créé',
          });
        }
      }
    },
    saveBirth(date) {
      this.$refs.birthMenu.save(date);
    },
  },
  watch: {
    birthMenuIsActive(val) {
      if (val) {
        setTimeout(() => {
          this.$refs.birthPicker.activePicker = 'YEAR';
        });
      }
    },
  },
};
</script>
